import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, RotateCcw } from "lucide-mui";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  api,
  type ProspectDetail,
  type ProspectHit,
  type ProspectListContactEntry,
  type ProspectListMembership,
} from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import CountryName from "../../components/CountryName.tsx";
import DetailDrawer from "../../components/DetailDrawer.tsx";
import { FoldList, FoldRow, useOpenFolds } from "../../components/FoldList.tsx";
import FormattedDate from "../../components/FormattedDate.tsx";
import RelativeTime from "../../components/RelativeTime.tsx";
import SectionHeading from "../../components/SectionHeading.tsx";
import ContactCard from "../../components/contacts/ContactCard.tsx";
import { floodOrigin, type FloodOrigin } from "../../components/contacts/flood.ts";
import AgentRunTranscript from "../../components/agent/AgentRunTranscript.tsx";
import { useCanSeeTranscripts } from "../../components/agent/useCanSeeTranscripts.ts";
import ProspectCompanyDetails, {
  WebsiteLink,
  prospectWebsite,
} from "../../components/prospects/ProspectCompanyDetails.tsx";
import ProspectHitTimeline from "../../components/prospects/ProspectHitTimeline.tsx";
import {
  CollectionContactsSection,
  CollectionEntrySection,
} from "../../components/prospects/collection/CollectionEntrySection.tsx";
import ProspectSourcesCell from "../../components/prospects/ProspectSourcesCell.tsx";
import PursueReviewToggle from "../../components/review/PursueReviewToggle.tsx";
import ReviewDecisionDialog from "../../components/review/ReviewDecisionDialog.tsx";
import ReviewStatusChips from "../../components/review/ReviewStatusChips.tsx";
import ShortcutHints from "../../components/review/ShortcutHints.tsx";
import { SnoozeMenuPopover } from "../../components/review/SnoozeMenu.tsx";
import { REVIEW_LABELS, canMarkDone, canReview, canSnooze } from "../../components/review/rules.ts";
import { DEFAULT_SNOOZE_DAYS, inDays, isSnoozed } from "../../components/review/snooze.ts";
import type { ReviewDecision } from "../../components/review/types.ts";
import SignalResearchPanel from "../../components/signals/SignalResearchPanel.tsx";
import {
  ContinueStepButton,
  ContinueStepsSplitButton,
  useContinuableSteps,
} from "../../components/workflows/StepApprovalBanner.tsx";
import ExecutionStatusChip, { executionGist } from "../../components/workflows/ExecutionStatusChip.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { collectionPath, useTenant } from "../../contexts/TenantContext.tsx";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts.ts";
import { formatNumber } from "../../utils/format.ts";
import { isJobActive } from "../../utils/status.ts";
import ContactDrawer from "../contacts/ContactDrawer.tsx";
import { PROSPECT_REVIEW_COPY, useProspectReview } from "./prospectReview.ts";
import SignalListChip from "../prospect-list-detail/components/SignalListChip.tsx";
import StatusChip from "../../components/StatusChip.tsx";

function ContactsSection({
  contacts,
  openEntryId,
  onOpen,
}: {
  contacts: ProspectListContactEntry[];
  openEntryId: string | null;
  onOpen: (contact: ProspectListContactEntry, el: HTMLElement) => void;
}) {
  return (
    <Stack spacing={1}>
      {contacts.map((c) => (
        <ContactCard key={c.id} contact={c} selected={c.id === openEntryId} onOpen={(el) => onOpen(c, el)} />
      ))}
    </Stack>
  );
}

function ListsSection({ lists }: { lists: ProspectListMembership[] }) {
  const { tenantPath } = useTenant();
  return (
    <Stack spacing={0.75}>
      {lists.map((l) => (
        <Stack key={l.entry_id} direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Link component={RouterLink} to={tenantPath(collectionPath(l.collection_id))} variant="body2">
            {l.collection_name}
          </Link>
          {l.collection_type === "signal" && <SignalListChip />}
          {l.archived && <Chip size="small" variant="outlined" label="Archived" />}
          <StatusChip kind="prospect_status" value={l.review_state} variant="outlined" />
          {(l.inclusion_reason || l.disqualification_reason) && (
            <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
              {l.review_state === "disqualified" && l.disqualification_reason
                ? l.disqualification_reason
                : l.inclusion_reason}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Everything a reviewer can do with the open prospect, in one row: Pursue /
 * Pursue & snooze / Leave / Disqualify and the state it leaves it in on the
 * left; continue a parked step and mark it done on the right. P, S and D open
 * the same decisions from the keyboard.
 */
function DrawerActions({
  prospect,
  canContinue,
  onDecided,
  onReviewed,
  keysActive,
}: {
  prospect: ProspectDetail;
  canContinue: boolean;
  /** Off while a drawer beside it (a person) has the keys. */
  keysActive: boolean;
  onDecided: () => void;
  onReviewed?: (decision: ReviewDecision | { action: "done" | "reopen" }) => void;
}) {
  const { review, snooze, done, continueSteps, busy } = useProspectReview();
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  const [snoozeAnchor, setSnoozeAnchor] = useState<HTMLElement | null>(null);
  const reviewable = canReview(prospect);
  const pursued = prospect.review_state === "accepted";
  const snoozed = isSnoozed(prospect.snoozed_until);
  const snoozable = canSnooze(prospect);
  const finishable = canMarkDone(prospect);
  const isDone = !!prospect.done_at;
  const { steps, sourceLabel } = useContinuableSteps(prospect.executions);
  const ids = [prospect.id];
  const decide = (action: ReviewDecision["action"], snoozeToo?: boolean) => {
    if (reviewable && !busy) setDecision({ ids, action, snooze: snoozeToo });
  };
  const pursueAndSnooze = () => {
    if (!pursued) return decide("accept", true);
    if (busy || !snoozable) return;
    snooze.mutate({ ids, until: inDays(DEFAULT_SNOOZE_DAYS) });
    onReviewed?.({ ids, action: "accept", snooze: true });
  };
  useKeyboardShortcuts(
    {
      p: () => !pursued && decide("accept"),
      s: pursueAndSnooze,
      d: () => prospect.review_state !== "disqualified" && decide("disqualify"),
    },
    keysActive && decision === null && snoozeAnchor === null,
  );
  const approve = (executionId: string) => {
    const step = steps.find((s) => s.id === executionId);
    if (!step?.signal_id) return Promise.reject(new Error("No signal behind this step"));
    return api.approveSignalStep(step.signal_id, executionId);
  };
  return (
    <>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
          <Tooltip title={reviewable ? "" : PROSPECT_REVIEW_COPY.notReviewable}>
            <span>
              <PursueReviewToggle
                value={prospect.review_state}
                disabled={busy || !reviewable}
                pursueAndSnooze={{
                  onClick: (anchor) => (snoozable ? setSnoozeAnchor(anchor) : decide("accept", true)),
                  snoozed,
                  title: !snoozable ? (
                    "Pursue and put it aside on the worklist until a day · S"
                  ) : snoozed ? (
                    <>
                      Snoozed until <FormattedDate value={prospect.snoozed_until!} />
                    </>
                  ) : (
                    "Snooze · S"
                  ),
                  disabled: pursued && !snoozable,
                }}
                onChange={(next) => {
                  if (next === null) return;
                  if (next === "pending") review.mutate({ ids, action: "reset" });
                  else decide(next === "accepted" ? "accept" : "disqualify");
                }}
              />
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1, minWidth: 0, pl: 0.5 }}>
            <Typography variant="body2" noWrap>
              {isDone ? "Done" : REVIEW_LABELS[prospect.review_state]}
            </Typography>
            {snoozed && (
              <Typography variant="caption" noWrap sx={{ color: "text.secondary", display: "block" }}>
                Snoozed until <FormattedDate value={prospect.snoozed_until!} />
              </Typography>
            )}
          </Box>
          {canContinue && steps.length === 1 && (
            <ContinueStepButton execution={steps[0]} approve={approve} onDecided={onDecided} sourceLabel={sourceLabel} />
          )}
          {canContinue && steps.length > 1 && (
            <ContinueStepsSplitButton
              steps={steps}
              approve={approve}
              onDecided={onDecided}
              sourceLabel={sourceLabel}
              onContinueAll={() => continueSteps.mutate(ids)}
              continuingAll={continueSteps.isPending}
            />
          )}
          <Tooltip title={finishable || isDone ? "" : PROSPECT_REVIEW_COPY.notOnWorklist}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color={isDone ? "inherit" : "success"}
                startIcon={isDone ? <RotateCcw /> : <CircleCheck />}
                disabled={busy || !finishable}
                onClick={() => {
                  done.mutate({ ids, done: !isDone });
                  onReviewed?.({ action: isDone ? "reopen" : "done" });
                }}
              >
                {isDone ? "Reopen" : "Mark done"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        <SnoozeMenuPopover
          anchor={snoozeAnchor}
          onClose={() => setSnoozeAnchor(null)}
          snoozedUntil={prospect.snoozed_until}
          onSnooze={(until) => snooze.mutate({ ids, until })}
        />
        <ReviewDecisionDialog
          decision={decision}
          onClose={() => setDecision(null)}
          onSubmitted={onReviewed}
          useReview={useProspectReview}
          copy={PROSPECT_REVIEW_COPY}
        />
    </>
  );
}

interface Props {
  prospectId: string | null;
  onClose: () => void;
  canDecide: boolean;
  canContinue: boolean;
  // Pursued, disqualified, snoozed or finished from the drawer: move on.
  onReviewed?: (decision: ReviewDecision | { action: "done" | "reopen" }) => void;
  // The prospect after / before this one in the list (J / ↓, K / ↑).
  onStep?: (direction: 1 | -1) => void;
  // Opened from this collection: its entry and people, with their actions.
  collectionId?: string;
  // The person open beside it (a master contact), and opening / closing one.
  contactId?: string | null;
  onOpenContact?: (contactId: string) => void;
  onCloseContact?: () => void;
}

/**
 * A prospect: the company, how often and by what it was prospected, every hit
 * with what is behind it (a signal's research, an agent's run), the people
 * found there, and, folded at the bottom, the collections it is on and its
 * workflow steps. Opened from a collection, its entry there and the people on
 * it come first, editable.
 * A person's card opens them in a `ContactDrawer` out of its left edge; this
 * one stays as it is.
 */
export default function ProspectDrawer({
  prospectId,
  onClose,
  canDecide,
  canContinue,
  onReviewed,
  onStep,
  collectionId,
  contactId = null,
  onOpenContact,
  onCloseContact,
}: Props) {
  const queryClient = useQueryClient();
  const { tenantPath } = useTenant();
  const { user } = useAuth();
  // (Re-)running a signal's research spends the org's budget: the scope that edits workflows.
  const canResearch = userHasGlobalScope(user, "signal_rules:write");
  const canSeeTranscripts = useCanSeeTranscripts();
  const [expandedHit, setExpandedHit] = useState<string | null>(null);
  // Collections and workflow steps, folded at the bottom; folded again on the next prospect.
  const folds = useOpenFolds(prospectId ?? "");
  const { data, isLoading, error } = useQuery({
    queryKey: keys.prospect.detail(prospectId ?? ""),
    queryFn: () => api.getProspect(prospectId!),
    enabled: !!prospectId,
    meta: { live: true },
  });
  const refresh = () => {
    if (prospectId) queryClient.invalidateQueries({ queryKey: keys.prospect.detail(prospectId) });
    queryClient.invalidateQueries({ queryKey: keys.prospect.all });
  };

  // The newest signal hit opens with its research, so the brief is one glance away.
  const hits = data?.hits ?? [];
  const firstSignal = hits.find((h) => h.kind === "signal" && h.signal)?.id ?? null;
  const [autoOpenedFor, setAutoOpenedFor] = useState<string | null>(null);
  if (data && autoOpenedFor !== data.id) {
    setAutoOpenedFor(data.id);
    setExpandedHit(firstSignal);
  }

  const membership = collectionId ? data?.collections.find((c) => c.collection_id === collectionId) : undefined;
  // Without the collection's own section, every collection's people, read-only.
  const otherContacts = membership
    ? (data?.contacts ?? []).filter((c) => c.collection_id !== membership.collection_id)
    : (data?.contacts ?? []);

  const website = data ? prospectWebsite(data) : null;

  // The people in the order the drawer shows them, for J / K in the contact drawer.
  const collectionContacts = membership
    ? (data?.contacts ?? []).filter((c) => c.collection_id === membership.collection_id)
    : [];
  const people = [...collectionContacts, ...otherContacts];
  const [origin, setOrigin] = useState<FloodOrigin | null>(null);
  // The card clicked, among the person's entries on several collections.
  const [clickedEntry, setClickedEntry] = useState<string | null>(null);
  const openEntry = contactId
    ? (people.find((c) => c.id === clickedEntry && c.master_contact_id === contactId) ??
      people.find((c) => c.master_contact_id === contactId))
    : undefined;
  const openContact = (c: ProspectListContactEntry, el: HTMLElement | null) => {
    if (!onOpenContact) return;
    // The open card again closes its person.
    if (c.id === openEntry?.id) return onCloseContact?.();
    setClickedEntry(c.id);
    // From a card on screen the colour floods; from one scrolled away it just swaps.
    const rect = el?.getBoundingClientRect();
    const onScreen = !!rect && rect.bottom > 0 && rect.top < window.innerHeight;
    setOrigin(el && onScreen ? floodOrigin(el, c.master_contact_id) : null);
    if (c.master_contact_id !== contactId) onOpenContact(c.master_contact_id);
  };
  const openIndex = openEntry ? people.indexOf(openEntry) : -1;
  const stepContact = (direction: 1 | -1) => {
    const next = people[openIndex + direction];
    if (openIndex < 0 || !next) return;
    openContact(next, document.querySelector(`[data-contact-entry="${CSS.escape(next.id)}"]`));
  };

  const renderExpanded = (hit: ProspectHit) => {
    if (hit.kind === "signal" && hit.signal) {
      return <SignalResearchPanel signalId={hit.signal.id} canResearch={canResearch} />;
    }
    if (hit.agent_run && data && canSeeTranscripts) {
      return (
        <AgentRunTranscript
          runId={hit.agent_run.id}
          initiallyActive={isJobActive(hit.agent_run.status)}
          fetchPage={(afterSeq) => api.getProspectHitTranscript(data.id, hit.id, afterSeq)}
        />
      );
    }
    return null;
  };

  const title = data ? (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
      {data.master_company_id ? (
        <Link component={RouterLink} to={tenantPath(`/companies/${data.master_company_id}`)} color="inherit" underline="hover">
          {data.company_name ?? "Company"}
        </Link>
      ) : (
        <span>{data.company_name ?? "Company"}</span>
      )}
      {!data.resolved && (
        <Tooltip title="Not matched to a company in the database yet; link it from the collection it is in">
          <Chip size="small" variant="outlined" color="warning" label="Not in database" />
        </Tooltip>
      )}
    </Stack>
  ) : (
    "Prospect"
  );

  return (
    <DetailDrawer
      open={!!prospectId}
      // Esc with the focus in here closes the person first.
      onClose={(reason) => (reason === "escapeKeyDown" && contactId ? onCloseContact?.() : onClose())}
      onStep={onStep}
      aside={{
        open: !!contactId,
        render: (edge) => (
          <ContactDrawer
            contactId={prospectId ? contactId : null}
            entry={openEntry}
            edge={edge}
            origin={origin}
            position={openIndex >= 0 ? { index: openIndex, total: people.length } : undefined}
            onStep={stepContact}
            onClose={() => onCloseContact?.()}
          />
        ),
      }}
      overline={
        data ? (
          <>
            Prospect
            {data.company_country && (
              <>
                {" · "}
                <CountryName code={data.company_country} />
              </>
            )}
          </>
        ) : (
          "Prospect"
        )
      }
      title={title}
      actions={
        data &&
        canDecide && (
          <DrawerActions
            prospect={data}
            canContinue={canContinue}
            keysActive={!contactId}
            onDecided={refresh}
            onReviewed={onReviewed}
          />
        )
      }
      hints={onStep && <ShortcutHints canDecide={canDecide} />}
    >
      {isLoading && <CircularProgress size={20} />}
      {error && <Alert severity="error">Could not load this prospect.</Alert>}
      {data && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="subtitle2">
                  Prospected {formatNumber(data.hit_count)} {data.hit_count === 1 ? "time" : "times"}
                </Typography>
                <ReviewStatusChips subject={data} reopenedAt={data.reopened_at} newHits={data.hits_since_decision} />
              </Stack>
              {website ? (
                <WebsiteLink url={website} />
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No website on record
                </Typography>
              )}
              <ProspectSourcesCell sources={data.sources} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                First <FormattedDate value={data.first_hit_at} /> · last <RelativeTime value={data.last_hit_at} />
                {data.collection_count > 0 && ` · on ${data.collection_count} ${data.collection_count === 1 ? "collection" : "collections"}`}
              </Typography>
            </Stack>
          </Paper>

          {data.master_company_id && data.company && (
            <ProspectCompanyDetails companyId={data.master_company_id} company={data.company} />
          )}

          {membership && (
            <CollectionEntrySection prospect={data} membership={membership} canEdit={canDecide} onGone={onClose} />
          )}
          {membership && (
            <CollectionContactsSection
              prospect={data}
              membership={membership}
              openEntryId={openEntry?.id ?? null}
              onOpenContact={openContact}
            />
          )}

          <Box>
            <SectionHeading title="Prospecting history" count={hits.length} />
            <ProspectHitTimeline
              hits={hits}
              prospectId={data.id}
              expandedHitId={expandedHit}
              onToggle={(id) => setExpandedHit((prev) => (prev === id ? null : id))}
              renderExpanded={renderExpanded}
            />
          </Box>

          {otherContacts.length > 0 && (
            <Box sx={{ pt: 1 }}>
              <SectionHeading
                title={membership ? "Contacts on other collections" : "Contacts"}
                count={otherContacts.length}
              />
              <ContactsSection contacts={otherContacts} openEntryId={openEntry?.id ?? null} onOpen={openContact} />
            </Box>
          )}

          {(data.collections.length > 0 || data.executions.length > 0) && (
            <Box sx={{ pt: 1 }}>
              <FoldList>
                {data.collections.length > 0 && (
                  <FoldRow
                    label="Collections"
                    summary={`${formatNumber(data.collections.length)} · ${data.collections.map((c) => c.collection_name).join(", ")}`}
                    expanded={folds.isOpen("collections")}
                    onToggle={() => folds.toggle("collections")}
                  >
                    <ListsSection lists={data.collections} />
                  </FoldRow>
                )}
                {data.executions.length > 0 && (
                  <FoldRow
                    label="Workflow steps"
                    summary={executionGist(data.executions)}
                    expanded={folds.isOpen("steps")}
                    onToggle={() => folds.toggle("steps")}
                  >
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                      {data.executions.map((e) => (
                        <ExecutionStatusChip key={e.id} execution={e} />
                      ))}
                    </Stack>
                  </FoldRow>
                )}
              </FoldList>
            </Box>
          )}
        </Stack>
      )}
    </DetailDrawer>
  );
}
